import { DEFAULT_CLOSE_MESSAGES_ON_PAGECHANGE, DEFAULT_REUSE_MESSAGES_ON_PAGECHANGE } from './constants';
import { ErrorNotifier } from './error-notifier';
import { EventChunkUploader } from './event-chunk-uploader';
import { EventLog } from './event-log';
import { PrebuildMessage } from './message.types';
import { Message } from './messages/message';
import { MessagePersistence } from './messages/message-persistence';
import { SocialProofMessage } from './messages/social-proof-message';
import { SilverEggSetting } from './recommendation.types';
import { TrackerMessageReuseType } from './tracker.types';
import { TriggeredEvent } from './triggered-event.types';
import { console } from './utility/console';

// TODO: いいかんじの置き場所を決める
type Idfv = string;
type UserAnnotation = string;
type SdkToken = string;
type EventTrackCallback = (eventName: string) => void; // TODO: 名前もう少し考える
type MessageReuseType = TrackerMessageReuseType;

export class MessageManager {
  public buildMessagesComplete: boolean;
  private messages: Message[];
  private inactiveMessagesToReviveOnPagechange: Message[];
  public canShowNonSimultaneouslyDisplayableMessage: boolean;
  public reuseMessagesOnPagechange: MessageReuseType = DEFAULT_REUSE_MESSAGES_ON_PAGECHANGE;

  private token: SdkToken;
  private idfv: Idfv;
  private userAnnotation: UserAnnotation;

  private eventChunkUploader?: EventChunkUploader;
  private eventLog: EventLog | null;
  public defaultCloseOnPagechange: boolean = DEFAULT_CLOSE_MESSAGES_ON_PAGECHANGE;

  public constructor() {
    this.buildMessagesComplete = false;
    this.messages = [];
    this.inactiveMessagesToReviveOnPagechange = [];
    this.canShowNonSimultaneouslyDisplayableMessage = true;

    this.token = '';
    this.idfv = '';
    this.userAnnotation = '';
    this.eventLog = null;
  }

  public clearCurrentlyDisplayableMessages(): void {
    this.hideVisibleMessages();
    this.messages = [];
    this.inactiveMessagesToReviveOnPagechange = [];
    this.buildMessagesComplete = false;
  }

  public clearShownMessages(): void {
    MessagePersistence.clearShownMessages(this.token);
  }

  public markBuildMessageComplete(): void {
    this.buildMessagesComplete = true;
  }

  public activate({
    token = '',
    idfv = '',
    userAnnotation = '',
    eventChunkUploader,
    eventLog,
  }: {
    token?: SdkToken;
    idfv?: Idfv;
    userAnnotation?: UserAnnotation;
    eventChunkUploader: EventChunkUploader;
    eventLog: EventLog | null;
  }) {
    this.token = token;
    this.idfv = idfv;
    this.userAnnotation = userAnnotation;
    this.eventChunkUploader = eventChunkUploader;
    this.eventLog = eventLog;
  }

  // TODO: 引数の messages の型の名前考える
  public buildMessages({
    messages = [],
    silverEggSetting,
    onEventTrack,
  }: {
    messages: Readonly<PrebuildMessage[]>;
    silverEggSetting: Readonly<SilverEggSetting>;
    onEventTrack?: EventTrackCallback;
  }): Promise<void> {
    // In case configure is not completed
    if (!this.eventChunkUploader || !this.eventLog) {
      return Promise.resolve();
    }

    return Promise.all(messages.map((message) => this.buildMessage(message, silverEggSetting, onEventTrack)))
      .then(() => {
        this.markBuildMessageComplete();
      })
      .catch((err) => {
        const error = err;
        // FIXME: この error どこにも throw なり reject されてないのでどうにかする
        error.message = `Message Manager failed to build messages (${error.message})`;
      });
  }

  // TODO: 引数で渡ってくる message と Message クラスを具体化した message(msg) をどうにか言いわける
  private buildMessage(
    message: Readonly<PrebuildMessage>,
    silverEggSetting: Readonly<SilverEggSetting>,
    onEventTrack?: EventTrackCallback
  ): Promise<void> {
    return new Promise((resolve) => {
      let msg;

      const { panels, kind } = message;
      const MessageClass = panels?.[0].social_proof_event ? SocialProofMessage : Message;
      const isControlGroup = kind === 'control_group'; // TODO: message.kindは将来的に廃止したい。(サーバサイドで廃止できたらこちらも廃止する)

      try {
        msg = new MessageClass({
          message: { ...message, close_on_pagechange: message.close_on_pagechange ?? this.defaultCloseOnPagechange },
          manager: this,
          idfv: this.idfv,
          token: this.token,
          eventChunkUploader: this.eventChunkUploader!,
          eventLog: this.eventLog!, // TODO: msg.isConditionSatisfied(event) の扱い考える
          isControlGroup,
          silverEggSetting,
          onActionButtonClick: (_, __, eventName) => eventName && onEventTrack?.(eventName),
          onMessageShow: (messageToshow: Message) => {
            this.markAsShown({
              message: messageToshow,
              token: this.token,
              idfv: this.idfv,
              userAnnotation: this.userAnnotation,
            });

            if (isControlGroup) {
              messageToshow.close();
            }
          },
          onMessageClose: (messageToRemove: Message) => this.removeMessage(messageToRemove),
        });
      } catch (error: any) {
        ErrorNotifier.notify({ error, extraPayload: message });
        return resolve();
      }

      this.messages.push(msg);
      if (msg.imageUrl) {
        msg.preloadImage();
      }
      return resolve();
    });
  }

  public async showTriggeredMessages(event: TriggeredEvent): Promise<void> {
    if (!event || !event.name) return Promise.resolve();

    try {
      const messages = await this.displayableMessages(event);
      // isSimultaneouslyDisplayable というフラグによって次の2つの制御を行っている。
      //   1. 一斉に複数のメッセージを表示する
      //   2. isSimultaneouslyDisplayable = false なメッセージが画面上表示されている場合はつづけて isSimultaneouslyDisplayable = false を出してはいけない
      // 下記の一連の処理における変数名の長さや処理の複雑さは上記の2つの仕様を満たすために起因している。仕様が少しでも変われば手続きや変数名は変わるはずなので注意すべし。
      // 同時配信OKのメッセージは全て表示する
      const messagesToShow = messages.filter((msg) => msg.isSimultaneouslyDisplayable);

      // 同時配信NGのメッセージは優先度が一番高いもののうち先頭のものを表示する。なお優先度が同じ場合は最終更新時刻が新しいものが選ばれる。
      if (this.canShowNonSimultaneouslyDisplayableMessage) {
        const additionallyDisplayableMessage = messages
          .filter((msg) => !msg.isSimultaneouslyDisplayable)
          .sort((a, b) => {
            if (a.triggerPriority === b.triggerPriority) {
              return b.updatedAt - a.updatedAt;
            }
            return b.triggerPriority - a.triggerPriority;
          })[0];
        if (additionallyDisplayableMessage) {
          messagesToShow.push(additionallyDisplayableMessage);
        }
      }

      // Execute promisified methods (e.g. msg.prepareShowProcess()) in sequences, using Array.prototype.reduce
      // ref. https://azu.github.io/promises-book/#promise-sequence
      await messagesToShow.reduce(
        (chainedPromise: Promise<void>, msg) =>
          chainedPromise.then(async () => {
            // messagesToShow には現在画面上に表示中ものも含まれるので、これに該当するものはimpressionを複数記録してしまわないようスキップ
            if (msg.isCurrentlyVisible) return;

            try {
              const prepareSuccessful = await msg.prepareShowProcess({ event });
              if (!prepareSuccessful) return;

              await msg.startShowProcess();
            } catch (error) {
              console.warn(error);
            }
          }),
        Promise.resolve()
      );
    } catch (e) {
      console.error(e);
    }
  }

  public displayPreviewMessage(): void {
    if (this.messages.length === 0) {
      console.warn('No preview message was found. Seems like the preview URL is corrupt.');
      return;
    }
    if (!this.messages[0].isDOMReady) {
      console.warn('The preview message is not ready. Skipping.');
      return;
    }
    this.messages[0].startShowProcess();
  }

  public hideVisibleMessages(): void {
    const shownMessages = this.messages.filter((msg) => msg.isCurrentlyVisible);
    if (shownMessages.length > 0) {
      shownMessages.forEach((message) => message.clickClose());
    }
  }

  public removeMessage(message: Message): void {
    const idx = this.messages.indexOf(message);
    if (idx < 0) return;
    this.messages = this.messages.filter((_, i) => i !== idx);
    if (message.showEvenIfAlreadyRead) {
      if (
        // 無限回のメッセージのみを再利用する
        this.reuseMessagesOnPagechange === 'unlimited' &&
        !message.periodicImpressionLimit &&
        !message.totalImpressionLimit
      ) {
        this.inactiveMessagesToReviveOnPagechange.push(message);
      }
      if (
        // 無限回、もしくは有限の上限が設定され、上限に達していないメッセージを再利用する
        // (一日あたりの制限が設定されているものは、現状 showEvenIfAlreadyRead が偽なのでここに入らない)
        this.reuseMessagesOnPagechange === 'multitime' &&
        !message.periodicImpressionLimit &&
        (!message.totalImpressionLimit ||
          message.totalImpressionLimit - (message.totalImpressionCount ?? 0) > message.showCount)
      ) {
        this.inactiveMessagesToReviveOnPagechange.push(message);
      }
    }
  }

  public handlePagechange() {
    this.messages.forEach((m) => {
      try {
        m.handlePagechange();
      } catch (e) {
        console.error(e);
      }
    });
    this.messages = this.messages.concat(this.inactiveMessagesToReviveOnPagechange);
    this.inactiveMessagesToReviveOnPagechange = [];
  }

  private markAsShown({
    message,
    token,
    idfv,
    userAnnotation,
  }: {
    message: Message;
    token: SdkToken;
    idfv: Idfv;
    userAnnotation: UserAnnotation;
  }) {
    MessagePersistence.storeShownMessage(
      {
        idfv,
        userAnnotation,
        token,
      },
      message
    );
  }

  private displayableMessages(event: TriggeredEvent): Promise<Message[]> {
    const messages = this.messages.filter(
      (msg) =>
        msg.showEvenIfAlreadyRead ||
        !MessagePersistence.hasBeenDisplayed(
          {
            idfv: this.idfv,
            userAnnotation: this.userAnnotation,
            token: this.token,
          },
          msg
        )
    );
    return Promise.all(
      messages.map((msg) => {
        return msg.isConditionSatisfied(event).then((ok: boolean) => (ok ? msg : null));
      })
    ).then((displayables) => displayables.filter((message): message is Message => !!message && message.isDOMReady));
  }
}
